"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { ClipboardCheck, Compass, FileText, Hammer, CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
interface StepItem {
  number: string;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  image: {
    src: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
}
const steps: StepItem[] = [{
  number: "01",
  title: "Initial Consultation",
  description: "We visit your property to evaluate your existing outdoor space, discuss your layout goals, examine yard grades, and establish preliminary timelines and budgets.",
  icon: ClipboardCheck,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_consultation_assessment.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_consultation_assessment.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_consultation_assessment.webp-wvc-sizes",
    alt: "Contractor discussing backyard deck plans with homeowners on site"
  }
}, {
  number: "02",
  title: "Design & Planning",
  description: "Our design team creates 3D architectural deck renderings, drafting elevation schematics, structural footings, railing systems, and selecting composite or hardwood deck boards.",
  icon: Compass,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_design_blueprint_render.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_design_blueprint_render.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_design_blueprint_render.png-wvc-sizes",
    alt: "Architectural blueprints and material swatches for a custom outdoor deck"
  }
}, {
  number: "03",
  title: "Permitting & Approvals",
  description: "We prepare structural engineering documents and submit complete permit packages to local municipal building authorities, handling HOA reviews and code approvals.",
  icon: FileText,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_permitting_plan_review.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_permitting_plan_review.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_permitting_plan_review.png-wvc-sizes",
    alt: "Official architectural permits and building code site review documentation"
  }
}, {
  number: "04",
  title: "Construction & Craftsmanship",
  description: "Our carpenters pour concrete footings, frame joists with joist tape protection, lay premium decking, install secure railings, and integrate deck lighting fixtures.",
  icon: Hammer,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_framing_construction_build.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_framing_construction_build.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_framing_construction_build.png-wvc-sizes",
    alt: "Carpenters installing pressure-treated framing and composite deck boards"
  }
}, {
  number: "05",
  title: "Final Walkthrough & Handover",
  description: "We conduct a comprehensive punch-list walkthrough with you, review manufacturer warranties, provide seasonal care instructions, and officially sign off on your new deck.",
  icon: CheckCircle2,
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_completed_walkthrough_patio.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_completed_walkthrough_patio.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/deck_completed_walkthrough_patio.png-wvc-sizes",
    alt: "Completed multi-level outdoor deck with modern railing and outdoor seating"
  }
}];
export default function RemodelingProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section id="remodelingprocess" data-nav="light" ref={ref} className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className={`max-w-3xl mx-auto text-center mb-16 lg:mb-20 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider">
            Our 5-Step Method
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-5">
            A Clear, Predictable Deck Remodeling Process
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            From your very first idea to the finished outdoor living space, our proven five-stage sequence keeps every project organized, permitted, on schedule, and built to code.
          </p>
        </div>

        {/* Steps List */}
        <div className="space-y-12 lg:space-y-16">
          {steps.map((step, index) => {
          const Icon = step.icon;
          const isEven = index % 2 === 1;
          return <div key={step.number} data-index={index} className={`transition-all duration-700 delay-[${index * 100}ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                <Card className="border border-border/80 shadow-md overflow-hidden bg-card transition-shadow hover:shadow-lg">
                  <div className={`grid grid-cols-1 lg:grid-cols-12 items-center gap-0 ${isEven ? "lg:grid-flow-dense" : ""}`}>
                    {/* Visual Image */}
                    <div className={`relative w-full h-64 sm:h-72 lg:h-full min-h-[260px] lg:col-span-6 overflow-hidden ${isEven ? "lg:col-start-7" : "lg:col-start-1"}`}>
                      <img src={step.image.src} data-wvc-srcset={step.image.srcset} data-wvc-sizes={step.image.sizes} alt={step.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                      <div className="absolute top-4 left-4 lg:hidden">
                        <span className="font-mono text-xs font-semibold bg-background/90 text-foreground px-2.5 py-1 rounded shadow-xs border border-border">
                          Step {step.number}
                        </span>
                      </div>
                    </div>

                    {/* Step Details */}
                    <div className={`p-6 sm:p-8 lg:p-12 lg:col-span-6 flex flex-col justify-center ${isEven ? "lg:col-start-1" : "lg:col-start-7"}`}>
                      <div className="flex items-center gap-4 mb-4">
                        <span className="hidden lg:inline-block font-mono text-sm font-bold text-primary tracking-wider uppercase">
                          Step {step.number}
                        </span>
                        <div className="size-10 rounded-md bg-accent text-accent-foreground flex items-center justify-center shrink-0">
                          <Icon className="size-5 text-primary" />
                        </div>
                      </div>

                      <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground mb-3">
                        {step.title}
                      </h3>

                      <Separator className="my-2 bg-border/60 max-w-24" />

                      <CardContent className="px-0 py-3">
                        <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                          {step.description}
                        </p>
                      </CardContent>
                    </div>
                  </div>
                </Card>
              </div>;
        })}
        </div>

        {/* Section Callout / Next Step */}
        <div className={`mt-16 lg:mt-20 p-8 sm:p-10 rounded-xl bg-card border border-border text-center max-w-3xl mx-auto shadow-sm transition-all duration-700 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <h3 className="font-serif text-2xl font-bold text-foreground mb-3">
            Ready to Begin Step One?
          </h3>
          <p className="text-muted-foreground text-sm sm:text-base mb-6 max-w-xl mx-auto">
            Schedule an on-site consultation to review your property, explore materials, and receive a transparent project estimate.
          </p>
          <Button asChild size="lg" className="font-medium">
            <Link to="/ContactRequestAnEstimatePage">
              Schedule Your Consultation
              <ArrowRight className="size-4 ml-1.5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}